/**
 * Aggressive exact-cell Google Sheets rack parser.
 *
 * Supports wide rack layouts like:
 * Max Power | U | Rack 30 / S3-3A-04-13 | U | PSU Inlet | PSU Inlet | Device Label
 *
 * Supports:
 * - Racks side-by-side
 * - Racks below each other
 * - Exact grid display
 * - Aggressive rack extraction
 * - Full rack title reconstruction from nearby cells
 */

const cleanCell = (value) =>
  String(value || "")
    .replace(/\r/g, "")
    .replace(/\n/g, " ")
    .trim()
    .replace(/^"|"$/g, "")
    .trim();

const normalize = (value) =>
  cleanCell(value)
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeHeader = (value) => normalize(value).replace(/[.:]/g, "");

const parseCSV = (csvText) => {
  if (!csvText || !csvText.trim()) {
    return [];
  }

  const rows = [];
  let row = [];
  let current = "";
  let insideQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      current += '"';
      i++;
    } else if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === "," && !insideQuotes) {
      row.push(cleanCell(current));
      current = "";
    } else if ((char === "\r" || char === "\n") && !insideQuotes) {
      if (char === "\r" && nextChar === "\n") {
        i++;
      }

      row.push(cleanCell(current));
      rows.push(row);
      row = [];
      current = "";
    } else {
      current += char;
    }
  }

  if (current || row.length) {
    row.push(cleanCell(current));
    rows.push(row);
  }

  const maxCols = Math.max(...rows.map((row) => row.length), 0);

  return rows.map((row) => {
    const normalized = [...row];

    while (normalized.length < maxCols) {
      normalized.push("");
    }

    return normalized;
  });
};

const toNumber = (value, fallback = 0) => {
  const num = parseFloat(String(value || "").replace(/[^\d.-]/g, ""));
  return Number.isNaN(num) ? fallback : num;
};

const toInt = (value, fallback = 0) => {
  const num = parseInt(String(value || "").replace(/[^\d-]/g, ""), 10);
  return Number.isNaN(num) ? fallback : num;
};

const isUValue = (value) => {
  const num = toInt(value, -1);
  return num >= 1 && num <= 60;
};

const isRackTitle = (value) => {
  const text = cleanCell(value);
  const lower = normalize(value);

  if (!text) {
    return false;
  }

  return (
    lower.includes("rack") ||
    /rack\s*\d+/i.test(text) ||
    /rack\s*\d+\s*\/\s*s\d+/i.test(text) ||
    /s\d+\s*[-/]\s*\d*[a-z]?\s*[-/]\s*\d+/i.test(text)
  );
};

const isRackLocationCode = (value) => {
  const text = cleanCell(value);

  if (!text) {
    return false;
  }

  /**
   * Matches:
   * S3-3A-04-13
   * S3-A-08-21
   * S3/3A/04/13
   * S3-3A-04
   */
  return /s\d+\s*[-/]\s*[a-z0-9]+(?:\s*[-/]\s*[a-z0-9]+){1,6}/i.test(text);
};

const buildFullRackTitleFromNearbyCells = (grid, titleRow, titleCol) => {
  const baseTitle = cleanCell(grid[titleRow]?.[titleCol]);

  if (!baseTitle) {
    return "";
  }

  // If the title already has the full location, return it.
  if (baseTitle.includes("/") && isRackLocationCode(baseTitle)) {
    return baseTitle;
  }

  const nearbyLocations = [];

  for (
    let row = Math.max(0, titleRow - 3);
    row <= Math.min(grid.length - 1, titleRow + 3);
    row++
  ) {
    for (let col = Math.max(0, titleCol - 5); col <= titleCol + 8; col++) {
      const value = cleanCell(grid[row]?.[col]);

      if (!value) {
        continue;
      }

      if (isRackLocationCode(value) && !baseTitle.includes(value)) {
        nearbyLocations.push({
          value,
          distance: Math.abs(row - titleRow) + Math.abs(col - titleCol),
        });
      }
    }
  }

  nearbyLocations.sort((a, b) => a.distance - b.distance);

  const location = nearbyLocations[0]?.value || "";

  if (location) {
    // If base title is only Rack 30, combine with location.
    if (/rack\s*\d+/i.test(baseTitle) && !baseTitle.includes(location)) {
      return `${baseTitle} / ${location}`;
    }

    // If the cell is only location code, keep it.
    if (isRackLocationCode(baseTitle)) {
      return baseTitle;
    }

    return `${baseTitle} / ${location}`;
  }

  return baseTitle;
};

const isFooterText = (value) => {
  const text = normalize(value);

  return (
    text.includes("max load") ||
    text.includes("max load kw") ||
    text.includes("c20") ||
    text.includes("c14") ||
    text.includes("c16") ||
    text.includes("count") ||
    text.startsWith("pdu-") ||
    text.includes("#value")
  );
};

const isNonDeviceText = (value) => {
  const text = normalize(value);

  if (!text) {
    return true;
  }

  return (
    text === "u" ||
    text.includes("rack") ||
    text.includes("psu") ||
    text.includes("inlet") ||
    text.includes("serial no") ||
    text.includes("serial number") ||
    text.includes("sl no") ||
    text.includes("device label") ||
    text.includes("max power") ||
    text.includes("power total") ||
    text.includes("max load") ||
    text.includes("count") ||
    text.startsWith("pdu-") ||
    text === "#value!"
  );
};

const isLikelyDeviceName = (value) => {
  const text = cleanCell(value);

  if (!text) {
    return false;
  }

  if (isNonDeviceText(text)) {
    return false;
  }

  return /[a-zA-Z]/.test(text);
};

const isLikelySerialValue = (value) => {
  const text = cleanCell(value);
  const lower = normalize(value);

  if (!text || isSerialLabelHeader(text) || isNonDeviceText(text)) {
    return false;
  }

  if (["yes", "no", "true", "false", "0", "1"].includes(lower)) {
    return false;
  }

  return text.length >= 5 && /[a-zA-Z]/.test(text) && /\d/.test(text);
};

const countUValuesInColumnRange = (grid, col, startRow, endRow) => {
  let count = 0;

  for (let row = startRow; row <= endRow && row < grid.length; row++) {
    if (isUValue(grid[row]?.[col])) {
      count++;
    }
  }

  return count;
};

const findNearestUColumnLeft = (grid, titleRow, titleCol) => {
  for (let col = titleCol - 1; col >= Math.max(0, titleCol - 6); col--) {
    const count = countUValuesInColumnRange(
      grid,
      col,
      titleRow + 1,
      Math.min(grid.length - 1, titleRow + 70)
    );

    if (count >= 10) {
      return col;
    }
  }

  return null;
};

const findUSequencesInColumn = (grid, col) => {
  const sequences = [];
  let start = null;
  let count = 0;

  const closeSequence = (endRow) => {
    if (start !== null && count >= 20) {
      sequences.push({
        startRow: start,
        endRow,
        count,
      });
    }

    start = null;
    count = 0;
  };

  for (let row = 0; row < grid.length; row++) {
    if (isUValue(grid[row]?.[col])) {
      if (start === null) {
        start = row;
      }

      count++;
    } else {
      closeSequence(row - 1);
    }
  }

  closeSequence(grid.length - 1);

  return sequences;
};

const findRackTitleNear = (grid, sequenceStartRow, deviceCol) => {
  const rowStart = Math.max(0, sequenceStartRow - 10);
  const rowEnd = Math.min(grid.length - 1, sequenceStartRow + 4);
  const colStart = Math.max(0, deviceCol - 5);
  const colEnd = deviceCol + 8;

  let rackTitleCell = null;
  let rackLocationCell = null;

  for (let row = rowStart; row <= rowEnd; row++) {
    for (let col = colStart; col <= colEnd; col++) {
      const value = cleanCell(grid[row]?.[col]);

      if (!value) {
        continue;
      }

      if (!rackTitleCell && isRackTitle(value)) {
        rackTitleCell = {
          title: value,
          headerRow: row,
          titleCol: col,
        };
      }

      if (!rackLocationCell && isRackLocationCode(value)) {
        rackLocationCell = {
          value,
          headerRow: row,
          titleCol: col,
        };
      }
    }
  }

  if (rackTitleCell) {
    const fullTitle = buildFullRackTitleFromNearbyCells(
      grid,
      rackTitleCell.headerRow,
      rackTitleCell.titleCol
    );

    return {
      ...rackTitleCell,
      title: fullTitle,
    };
  }

  if (rackLocationCell) {
    return {
      title: rackLocationCell.value,
      headerRow: rackLocationCell.headerRow,
      titleCol: rackLocationCell.titleCol,
    };
  }

  return null;
};

const rangesOverlap = (aStart, aEnd, bStart, bEnd) => {
  return aStart <= bEnd && bStart <= aEnd;
};

const isDuplicateBlock = (blocks, block) => {
  return blocks.some((existing) => {
    const sameTitle =
      normalize(existing.title) &&
      normalize(block.title) &&
      normalize(existing.title) === normalize(block.title);

    const sameColumn = Math.abs(existing.deviceCol - block.deviceCol) <= 2;

    const sameVerticalArea = rangesOverlap(
      existing.startRow,
      existing.endRow,
      block.startRow,
      block.endRow
    );

    return sameTitle || (sameColumn && sameVerticalArea);
  });
};

const detectBlocksByTitles = (grid) => {
  const blocks = [];

  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      const cell = cleanCell(grid[row][col]);

      if (!isRackTitle(cell)) {
        continue;
      }

      const leftUCol = findNearestUColumnLeft(grid, row, col);
      if (leftUCol === null) {
        continue;
      }

      const sequence = findUSequencesInColumn(grid, leftUCol).find(
        (candidate) =>
          candidate.startRow > row &&
          candidate.startRow <= row + 10
      );

      if (!sequence) {
        continue;
      }

      const startRow = sequence.startRow;
      const endRow = sequence.endRow;
      const deviceCol = leftUCol + 1;
      const rightUCol = leftUCol + 2;

      const block = {
        title: buildFullRackTitleFromNearbyCells(grid, row, col),
        headerRow: row,
        startRow,
        endRow,
        powerCol: Math.max(0, leftUCol - 1),
        leftUCol,
        deviceCol,
        rightUCol,
        psu1Col: rightUCol + 1,
        psu2Col: rightUCol + 2,
        labelCol: rightUCol + 3,
      };

      if (!isDuplicateBlock(blocks, block)) {
        blocks.push(block);
      }
    }
  }

  return blocks;
};

const detectBlocksByUSequences = (grid, existingBlocks) => {
  const blocks = [...existingBlocks];
  const maxCols = Math.max(...grid.map((row) => row.length), 0);

  for (let col = 0; col < maxCols; col++) {
    const leftSequences = findUSequencesInColumn(grid, col);

    if (!leftSequences.length) {
      continue;
    }

    const possibleDeviceCol = col + 1;
    const possibleRightUCol = col + 2;

    for (const sequence of leftSequences) {
      const rightUCount = countUValuesInColumnRange(
        grid,
        possibleRightUCol,
        sequence.startRow,
        sequence.endRow
      );

      if (rightUCount < 20) {
        continue;
      }

      const nearbyTitle = findRackTitleNear(
        grid,
        sequence.startRow,
        possibleDeviceCol
      );

      const block = {
        title:
          nearbyTitle?.title ||
          `Rack block R${sequence.startRow + 1} C${possibleDeviceCol + 1}`,
        headerRow: nearbyTitle?.headerRow ?? Math.max(0, sequence.startRow - 1),
        startRow: sequence.startRow,
        endRow: sequence.endRow,
        powerCol: Math.max(0, col - 1),
        leftUCol: col,
        deviceCol: possibleDeviceCol,
        rightUCol: possibleRightUCol,
        psu1Col: possibleRightUCol + 1,
        psu2Col: possibleRightUCol + 2,
        labelCol: possibleRightUCol + 3,
      };

      if (!isDuplicateBlock(blocks, block)) {
        blocks.push(block);
      }
    }
  }

  return blocks.sort((a, b) => {
    if (a.startRow !== b.startRow) {
      return a.startRow - b.startRow;
    }

    return a.deviceCol - b.deviceCol;
  });
};

const refineBlockRange = (grid, block) => {
  let startRow = block.startRow;
  let endRow = block.endRow;

  for (let row = block.headerRow + 1; row < grid.length; row++) {
    const leftU = grid[row]?.[block.leftUCol];
    const rightU = grid[row]?.[block.rightUCol];

    if (isUValue(leftU) || isUValue(rightU)) {
      startRow = row;
      break;
    }
  }

  for (let row = startRow; row <= endRow && row < grid.length; row++) {
    const blockText = [
      grid[row]?.[block.powerCol],
      grid[row]?.[block.leftUCol],
      grid[row]?.[block.deviceCol],
      grid[row]?.[block.rightUCol],
      grid[row]?.[block.psu1Col],
      grid[row]?.[block.psu2Col],
      grid[row]?.[block.labelCol],
    ].join(" ");

    if (isFooterText(blockText)) {
      endRow = row - 1;
      break;
    }

  }

  return {
    ...block,
    startRow,
    endRow,
  };
};

const getRowU = (row, block) => {
  return toInt(row?.[block.leftUCol], 0) || toInt(row?.[block.rightUCol], 0);
};

const isSerialLabelHeader = (value) => {
  const text = normalizeHeader(value);

  return (
    text === "serial no" ||
    text === "serial number" ||
    text === "sl no" ||
    text === "asset no" ||
    text === "asset number" ||
    text === "device label" ||
    text === "serial / label" ||
    text === "label"
  );
};

const findHeaderColumnNearBlock = (grid, block, predicate, fallbackCol) => {
  const rowStart = Math.max(0, block.headerRow - 2);
  const rowEnd = Math.min(grid.length - 1, block.startRow + 1);
  const colStart = Math.max(0, block.deviceCol - 2);
  const colEnd = block.deviceCol + 8;

  for (let row = rowStart; row <= rowEnd; row++) {
    for (let col = colStart; col <= colEnd; col++) {
      if (predicate(grid[row]?.[col])) {
        return col;
      }
    }
  }

  return fallbackCol;
};

const inferSerialColumnFromValues = (grid, block, fallbackCol) => {
  const colStart = Math.max(block.rightUCol + 1, block.deviceCol + 1);
  const colEnd = block.deviceCol + 8;
  let bestCol = fallbackCol;
  let bestScore = 0;

  for (let col = colStart; col <= colEnd; col++) {
    let score = 0;

    for (let row = block.startRow; row <= block.endRow && row < grid.length; row++) {
      const currentRow = grid[row] || [];

      if (!getRowU(currentRow, block)) {
        continue;
      }

      if (isLikelySerialValue(currentRow[col])) {
        score += 1;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestCol = col;
    }
  }

  return bestScore > 0 ? bestCol : fallbackCol;
};

const detectBlockColumns = (grid, block) => {
  const headerSerialCol = findHeaderColumnNearBlock(
    grid,
    block,
    isSerialLabelHeader,
    null
  );
  const serialCol = headerSerialCol !== null
    ? headerSerialCol
    : inferSerialColumnFromValues(grid, block, block.labelCol);

  return {
    ...block,
    serialCol,
    labelCol: serialCol,
  };
};

const detectRackBlocks = (grid) => {
  const byTitle = detectBlocksByTitles(grid);
  const allBlocks = detectBlocksByUSequences(grid, byTitle);

  return allBlocks.map((block) =>
    refineBlockRange(grid, detectBlockColumns(grid, block))
  );
};

const inferDeviceSizes = (devices) => {
  const sorted = [...devices].sort((a, b) => b.startU - a.startU);

  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];

    if (next) {
      current.size = Math.max(current.startU - next.startU, 1);
    } else if (i > 0) {
      current.size = Math.min(sorted[i - 1].size || 1, current.startU);
    } else {
      current.size = 1;
    }
  }

  return sorted;
};

const assignLabelsToDevices = (devices, labels) => {
  const sorted = [...devices].sort((a, b) => b.startU - a.startU);

  sorted.forEach((device, index) => {
    const nextDevice = sorted[index + 1];
    const bottomU = nextDevice
      ? nextDevice.startU + 1
      : Math.max(device.startU - device.size + 1, 1);

    const label = labels.find(
      (item) => item.u <= device.startU && item.u >= bottomU
    );

    if (label) {
      device.label = label.value;
      device.slNo = label.value;
      device.completed = true;
    }
  });

  return sorted;
};

const extractDevicesForBlock = (grid, block) => {
  const rawDevices = [];
  const labels = [];

  for (let row = block.startRow; row <= block.endRow; row++) {
    const currentRow = grid[row] || [];
    const u = getRowU(currentRow, block);

    if (!u) {
      continue;
    }

    const deviceName = cleanCell(currentRow[block.deviceCol]);
    const label = cleanCell(currentRow[block.labelCol]);

    if (label && isLikelySerialValue(label)) {
      labels.push({
        u,
        rowIndex: row,
        colIndex: block.labelCol,
        value: label,
      });
    }

    if (isLikelyDeviceName(deviceName)) {
      rawDevices.push({
        rowIndex: row,
        colIndex: block.deviceCol,
        startU: u,
        size: 1,
        maxPower: toNumber(currentRow[block.powerCol], 0),
        name: deviceName,
        psu1: toNumber(currentRow[block.psu1Col], 0),
        psu2: toNumber(currentRow[block.psu2Col], 0),
        label: "",
        slNo: "",
        status: "online",
        completed: false,
      });
    }
  }

  return assignLabelsToDevices(inferDeviceSizes(rawDevices), labels);
};

const findFlatHeaderIndex = (headers, patterns) =>
  headers.findIndex((header) =>
    patterns.some((pattern) => pattern.test(normalizeHeader(header)))
  );

const extractFlatRackRows = (grid) => {
  const headers = grid[0] || [];
  const rackCol = findFlatHeaderIndex(headers, [/^rack$/]);
  const powerCol = findFlatHeaderIndex(headers, [/^power$/, /^max power/, /^watts$/]);
  const uCol = findFlatHeaderIndex(headers, [/^u$/, /^start u$/, /^u position$/]);
  const deviceCol = findFlatHeaderIndex(headers, [/^device name$/, /^device$/, /^product id$/, /^description$/]);
  const serialCol = findFlatHeaderIndex(headers, [
    /^serial no$/,
    /^serial number$/,
    /^serial \/ label$/,
    /^sl no$/,
    /^asset no$/,
    /^label$/,
  ]);
  const psu1Col = findFlatHeaderIndex(headers, [/^psu1$/, /^psu 1$/]);
  const psu2Col = findFlatHeaderIndex(headers, [/^psu2$/, /^psu 2$/]);

  if (rackCol < 0 || uCol < 0 || deviceCol < 0) {
    return [];
  }

  const rackGroups = new Map();

  grid.slice(1).forEach((row, rowIndex) => {
    const title = cleanCell(row[rackCol]);
    const startU = toInt(row[uCol], 0);
    const name = cleanCell(row[deviceCol]);

    if (!title || !startU || !isLikelyDeviceName(name)) {
      return;
    }

    const serialValue = serialCol >= 0 ? cleanCell(row[serialCol]) : "";
    const serial = isLikelySerialValue(serialValue) ? serialValue : "";

    if (!rackGroups.has(title)) {
      rackGroups.set(title, []);
    }

    rackGroups.get(title).push({
      rowIndex: rowIndex + 1,
      colIndex: deviceCol,
      startU,
      size: 1,
      maxPower: powerCol >= 0 ? toNumber(row[powerCol], 0) : 0,
      name,
      psu1: psu1Col >= 0 ? toNumber(row[psu1Col], 0) : 0,
      psu2: psu2Col >= 0 ? toNumber(row[psu2Col], 0) : 0,
      label: serial,
      slNo: serial,
      status: "online",
      completed: Boolean(serial),
    });
  });

  return Array.from(rackGroups, ([title, devices]) => ({
    title,
    block: null,
    devices: inferDeviceSizes(devices),
  }));
};

const buildCellMeta = (grid, blocks) => {
  const meta = {};

  const mark = (row, col, type, blockTitle = "") => {
    if (row < 0 || col < 0) {
      return;
    }

    const key = `${row}:${col}`;

    meta[key] = {
      ...(meta[key] || {}),
      [type]: true,
      blockTitle,
    };
  };

  blocks.forEach((block) => {
    mark(block.headerRow, block.deviceCol, "rackTitle", block.title);

    // Also highlight nearby location cells if present.
    for (
      let row = Math.max(0, block.headerRow - 2);
      row <= Math.min(grid.length - 1, block.headerRow + 2);
      row++
    ) {
      for (let col = Math.max(0, block.deviceCol - 4); col <= block.deviceCol + 6; col++) {
        if (isRackLocationCode(grid[row]?.[col])) {
          mark(row, col, "rackTitle", block.title);
        }
      }
    }

    for (let row = block.startRow; row <= block.endRow; row++) {
      mark(row, block.powerCol, "powerArea", block.title);
      mark(row, block.leftUCol, "uCell", block.title);
      mark(row, block.rightUCol, "uCell", block.title);
      mark(row, block.deviceCol, "rackDeviceArea", block.title);
      mark(row, block.psu1Col, "psuArea", block.title);
      mark(row, block.psu2Col, "psuArea", block.title);
      mark(row, block.labelCol, "labelArea", block.title);
    }
  });

  return meta;
};

export const parseGoogleSheetCSV = (csvText) => {
  const grid = parseCSV(csvText);

  if (!grid.length) {
    return {
      grid: [],
      racks: [],
      detectedBlocks: [],
      cellMeta: {},
    };
  }

  const flatRacks = extractFlatRackRows(grid);
  if (flatRacks.length > 0) {
    return {
      grid,
      racks: flatRacks,
      detectedBlocks: [],
      cellMeta: {},
    };
  }

  const detectedBlocks = detectRackBlocks(grid);

  const racks = detectedBlocks
    .map((block) => ({
      title: block.title,
      block,
      devices: extractDevicesForBlock(grid, block),
    }))
    .filter((rack) => rack.devices.length > 0);

  const cellMeta = buildCellMeta(grid, detectedBlocks);

  console.log("Exact grid rows:", grid.length);
  console.log("Exact grid cols:", Math.max(...grid.map((row) => row.length), 0));
  console.log("Detected rack blocks:", detectedBlocks);
  console.log("Extracted racks:", racks);

  return {
    grid,
    racks,
    detectedBlocks,
    cellMeta,
  };
};

export const parseGoogleSheetJSON = (apiResponse) => {
  const values = apiResponse.values;

  if (!values || values.length === 0) {
    return {
      grid: [],
      racks: [],
      detectedBlocks: [],
      cellMeta: {},
    };
  }

  const csvText = values
    .map((row) =>
      row
        .map((cell) => {
          const value = String(cell || "");

          if (value.includes(",") || value.includes('"')) {
            return `"${value.replace(/"/g, '""')}"`;
          }

          return value;
        })
        .join(",")
    )
    .join("\n");

  return parseGoogleSheetCSV(csvText);
};

export const fetchGoogleSheetExactGrid = async (sheetId, gid = 0) => {
  const url = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${gid}&t=${Date.now()}`;

  const response = await fetch(url, { cache: "no-store" });

  if (!response.ok) {
    throw new Error(`Failed to fetch sheet CSV: ${response.status}`);
  }

  const csvText = await response.text();
  return parseGoogleSheetCSV(csvText);
};

export const fetchGoogleSheetCSV = fetchGoogleSheetExactGrid;

export const fetchFromGoogleSheetsAPI = async (
  sheetId,
  apiKey,
  sheetName = "Sheet1"
) => {
  const range = `${sheetName}!A1:ZZ5000`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${apiKey}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  const data = await response.json();
  return parseGoogleSheetJSON(data);
};

export const validateDeviceData = (device) => {
  const required = ["startU", "size", "name", "label", "completed"];
  return required.every((field) => field in device);
};

export const validateRackData = (rack) => {
  return (
    rack.title &&
    Array.isArray(rack.devices) &&
    rack.devices.every((device) => validateDeviceData(device))
  );
};

export const racksToCSV = (racks) => {
  const headers = [
    "Rack",
    "Power",
    "U",
    "Device Name",
    "Serial / Label",
    "PSU1",
    "PSU2",
    "Label",
    "Completed",
  ];

  const rows = [headers.join(",")];

  racks.forEach((rack) => {
    rack.devices.forEach((device) => {
      rows.push(
        [
          `"${rack.title || ""}"`,
          device.maxPower || "",
          device.startU || "",
          `"${device.name || ""}"`,
          `"${device.slNo || ""}"`,
          device.psu1 || "",
          device.psu2 || "",
          `"${device.label || ""}"`,
          device.completed ? "Yes" : "No",
        ].join(",")
      );
    });
  });

  return rows.join("\n");
};

export const downloadRacksAsCSV = (racks, filename = "rack-elevation.csv") => {
  const csv = racksToCSV(racks);
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
